import App from "../../../../vendor_core/core/src/App";
import GameEvents from "../../../../vendor_core/core/src/const/GameEvents";
import Layout from "../../../../vendor_core/core/src/utils/Layout";
import HTMLPopup from "../../../../vendor_core/core/src/views/components/HTMLPopup";
import HistoryHTMLPopup from "../../../../vendor_core/src/ui/popups/HistoryHTMLPopup";
import BURHistoryCarousel, { ICarouselSlide } from "./BURHistoryCarousel";
import { createSymbolCell } from "./BURHistoryCells";
import { JACKPOTS_FEATURE } from "./BURHistoryConstants";
import { createElement, HISTORY_CSS } from "./BURHistoryDom";
import BURHistoryFormatter from "./BURHistoryFormatter";
import { getSpinFrames } from "./BURHistoryFrames";
import { getReelsWidth } from "./BURHistoryLayout";
import { getFreeSpinWin, getSpinWin } from "./BURHistoryStats";
import BURHistoryStepper from "./BURHistoryStepper";
import { createSummaryTable } from "./BURHistorySummary";
import { historyText } from "./BURHistoryTexts";
import {
    IHistoryBonus,
    IHistorySessionData,
    IHistorySpin
} from "./BURHistoryTypes";
import {
    CASCADE_ATTRIBUTE,
    createWinsView,
    fitTableTexts
} from "./BURHistoryWins";

/**
 * Shows the last round from the getLastPlayHistory response, as a carousel: the round summary, the base spin, its
 * free spins, the replay spin and the replay's free spins, one page each.
 * When the server sends a rendered image (data URI string), the default vendor HistoryHTMLPopup is used.
 * The tables are only a fallback for when it sends the raw round data instead, as it currently does for BUR.
 */
export default class BURHistoryHTMLPopup extends HTMLPopup {
    protected container: HTMLElement;
    protected btnClose: HTMLButtonElement;
    protected defaultPopup: HistoryHTMLPopup;
    // Of the round being shown, set before its views are built
    private formatter: BURHistoryFormatter;
    // Stop their autoplay when the popup closes
    private steppers: BURHistoryStepper[] = [];
    // VendorWrapper emits RESIZE before it sets the canvas size on the page, so the reels are measured right after
    // that finishes (a microtask, so before the next paint) instead of in the event itself
    private onResize = () => {
        // HTMLPopupsManager removes the page without telling it when another history popup replaces it
        if (!this.container.isConnected) {
            this.dispose();
            return;
        }
        Promise.resolve().then(() => this.fitContainer());
    };
    private disposed = false;

    constructor(response: any, cb?: () => void) {
        super();

        const data = response?.data;
        if (typeof data === "string") {
            this.defaultPopup = new HistoryHTMLPopup(response, cb);
            return;
        }

        this.container = document.createElement("div");
        this._content = createElement("div", HISTORY_CSS.popup);
        this._content.appendChild(this.createContent(data?.sessionData));

        this.btnClose = this.createCloseButton();
        this.container.appendChild(this._content);
        this.container.appendChild(this.btnClose);
        this.container.setAttribute("class", HISTORY_CSS.container);
        this.fitContainer();

        App.dispatcher.addListener(GameEvents.RESIZE, this.onResize);

        this.btnClose.onclick = () => {
            this.dispose();
            cb?.();
        };
    }

    // Stops listening to the resize and the steppers' autoplay
    private dispose() {
        if (this.disposed) return;
        this.disposed = true;
        App.dispatcher.removeListener(GameEvents.RESIZE, this.onResize);
        this.steppers.forEach((stepper) => stepper.dispose());
    }

    public get content() {
        return this.defaultPopup ? this.defaultPopup.content : this.container;
    }

    // The page is as high as the screen and as wide as the reels (both used by the CSS)
    public fitContainer() {
        this.container.style.height = Layout.deviceHeight + "px";
        this.container.style.setProperty(
            "--history-width",
            `${getReelsWidth()}px`
        );
        // The tables changed width with the page
        fitTableTexts(this.container);
    }

    private createCloseButton(): HTMLButtonElement {
        const button = document.createElement("button");
        button.setAttribute("class", HISTORY_CSS.closeButton);
        const icon = document.createElement("span");
        icon.innerHTML = "&times";
        icon.setAttribute("class", HISTORY_CSS.closeButtonIcon);
        button.appendChild(icon);
        return button;
    }

    // The round, or a message when there is none
    private createContent(
        session: IHistorySessionData | undefined
    ): HTMLElement {
        if (!session?.playData?.gameData) {
            return createElement(
                "div",
                HISTORY_CSS.empty,
                historyText("empty")
            );
        }
        this.formatter = new BURHistoryFormatter(
            session.currency,
            parseFloat(session.playData.gameData.bet)
        );
        return this.createRoundView(session);
    }

    private createRoundView(session: IHistorySessionData): HTMLElement {
        const gameData = session.playData.gameData;
        const replay = gameData.replay;

        // First page: the round summary. Then, in play order: paid spin -> its free spins -> replay spin -> the
        // replay's free spins
        const slides: ICarouselSlide[] = [
            this.createSlide(() => [
                createSummaryTable(session, this.formatter)
            ]),
            this.createSpinSlide(historyText("baseSpin"), gameData),
            ...this.createBonusSlides(gameData.bonusInfo),
            ...(replay
                ? [
                      this.createSpinSlide(historyText("replaySpin"), replay),
                      ...this.createBonusSlides(replay.bonusInfo)
                  ]
                : [])
        ];

        const view = createElement("div", HISTORY_CSS.round);
        view.appendChild(
            new BURHistoryCarousel(
                slides,
                () => {
                    this.steppers.forEach((stepper) => stepper.pause());
                    // The page just shown is the one that has a size to fit the table texts to
                    fitTableTexts(this.container);
                },
                createElement("div", HISTORY_CSS.title, historyText("title"))
            ).element
        );
        return view;
    }

    // A page of the carousel, built when it is first shown
    private createSlide(createChildren: () => HTMLElement[]): ICarouselSlide {
        return {
            build: () => {
                const slide = createElement("div", HISTORY_CSS.slide);
                createChildren().forEach((child) => slide.appendChild(child));
                return slide;
            }
        };
    }

    // The base spin or the replay spin: its title with the win, then the spin
    private createSpinSlide(title: string, spin: IHistorySpin): ICarouselSlide {
        return this.createSlide(() => [
            this.createSectionTitle(title, getSpinWin(spin)),
            this.createSpinView(spin)
        ]);
    }

    // One slide per free spin of each free spins session
    private createBonusSlides(bonusInfo?: IHistoryBonus[]): ICarouselSlide[] {
        const slides: ICarouselSlide[] = [];
        (bonusInfo || []).forEach((bonus) => {
            // Jackpots can only be won in free spins with the Yellow feature
            const jackpotsEnabled = (bonus.combo || []).includes(
                JACKPOTS_FEATURE
            );
            (bonus.freeSpins || []).forEach((spin, index) =>
                slides.push(
                    this.createSlide(() => [
                        this.createSectionTitle(
                            `${historyText("freeSpin")} ${index + 1}`,
                            getFreeSpinWin(spin)
                        ),
                        this.createSpinView(spin, jackpotsEnabled)
                    ])
                )
            );
        });
        return slides;
    }

    private createSectionTitle(
        label: string,
        amount: number | undefined
    ): HTMLElement {
        return createElement(
            "div",
            HISTORY_CSS.section,
            this.formatter.withAmount(label, amount)
        );
    }

    // The spin's grid to step through, with its wins below
    private createSpinView(
        spin: IHistorySpin,
        jackpotsEnabled: boolean = false
    ): HTMLElement {
        const winsView = createWinsView(spin, jackpotsEnabled, this.formatter);
        // The cluster rows of each cascade, marked while the grid shows that cascade
        const clusterRows = Array.from(
            winsView.querySelectorAll(`tr[${CASCADE_ATTRIBUTE}]`)
        ).map((row) => ({
            row,
            cascade: Number(row.getAttribute(CASCADE_ATTRIBUTE))
        }));
        const stepper = new BURHistoryStepper({
            frames: getSpinFrames(spin, {
                start: historyText("startGrid"),
                cascade: historyText("cascade"),
                final: historyText("finalGrid")
            }),
            createSymbol: createSymbolCell,
            onFrame: (frame, animated) => {
                // Set before the rows change, so their highlight is delayed like the one of the symbols
                winsView.classList.toggle(HISTORY_CSS.winsAnimating, animated);
                clusterRows.forEach(({ row, cascade }) =>
                    row.classList.toggle(
                        HISTORY_CSS.activeRow,
                        cascade === frame.cascadeIndex
                    )
                );
            }
        });
        this.steppers.push(stepper);

        const view = createElement("div", HISTORY_CSS.spin);
        view.appendChild(stepper.element);
        view.appendChild(winsView);
        return view;
    }
}
